<template>
    <BaseDialog
        v-model:open="isOpen"
        :title="t('chat.workDocument.dialog.title')"
        :description="t('chat.workDocument.dialog.description')"
        show-close
        :reserve-mobile-nav-space="false"
        dialog-content-class="fs-mask">
        <div class="flex flex-col gap-4">
            <div class="flex justify-center">
                <div class="flex items-center gap-2">
                    <BaseChip size="2xs">{{ t("chat.workDocument.beta") }}</BaseChip>
                    <BasePopover v-if="allowAdvanced" as-child side="bottom" align="center" :side-offset="6" :collision-padding="12" content-class="w-80 p-4 sm:w-96">
                        <template #trigger>
                            <BaseButton theme="tertiary" size="sm" icon-only class="text-ink-muted" :aria-label="t('chat.workDocument.tutorial.trigger')">
                                <i class="bi bi-question-circle" aria-hidden="true"></i>
                            </BaseButton>
                        </template>
                        <template #content>
                            <div class="flex flex-col gap-3">
                                <p class="body-small-semibold">{{ t("chat.workDocument.tutorial.title") }}</p>
                                <img :src="visualDocumentTutorial" :alt="t('chat.workDocument.tutorial.alt')" class="w-full rounded-lgr border border-control-border" />
                                <p class="details text-ink-muted">{{ t("chat.workDocument.tutorial.body") }}</p>
                            </div>
                        </template>
                    </BasePopover>
                </div>
            </div>

            <ol v-if="isAdvanced" :aria-label="t('chat.workDocument.progress.label')" class="grid grid-cols-3">
                <li
                    v-for="step in progressSteps"
                    :key="step.id"
                    class="relative flex min-w-0 flex-col items-center gap-2 px-1 text-center"
                    :aria-current="currentStage === step.id ? 'step' : undefined">
                    <span
                        v-if="step.id > 1"
                        aria-hidden="true"
                        class="absolute top-3.5 right-[calc(50%+0.875rem)] h-px w-[calc(100%-1.75rem)]"
                        :class="currentStage >= step.id ? 'bg-button-primary' : 'bg-control-border'"></span>
                    <BaseButton
                        theme="tertiary"
                        class="relative flex flex-col items-center gap-2 p-0 hover:bg-transparent disabled:bg-transparent disabled:cursor-default"
                        :disabled="!canNavigateToStep(step.id)"
                        :aria-label="canNavigateToStep(step.id) ? t('chat.workDocument.progress.returnTo', { step: step.label }) : undefined"
                        @click="navigateToStep(step.id)">
                        <span
                            class="body-small-semibold flex size-7 items-center justify-center rounded-full"
                            :class="
                                currentStage === step.id
                                    ? 'bg-button-primary text-button-primary-text'
                                    : currentStage > step.id
                                      ? 'bg-surface-sunken text-ink'
                                      : 'border border-control-border bg-control-bg text-ink-muted'
                            ">
                            {{ step.id }}
                        </span>
                        <span class="details-big whitespace-nowrap" :class="currentStage === step.id ? 'text-ink' : 'text-ink-muted'">
                            {{ step.label }}
                        </span>
                    </BaseButton>
                </li>
            </ol>

            <div aria-live="polite" aria-atomic="true">
                <p v-if="isLoadingCapabilities" class="body-small text-ink-muted">{{ t("chat.workDocument.capabilities.loading") }}</p>
                <BaseNotification v-else-if="capabilitiesError" theme="error" hide-dismiss>
                    <template #content>
                        <div class="flex flex-col items-start gap-3">
                            <p class="body-small-semibold">{{ t("chat.workDocument.capabilities.unavailable") }}</p>
                            <BaseButton theme="secondary" size="sm" @click="fetchCapabilities()">{{ t("common.tryAgain") }}</BaseButton>
                        </div>
                    </template>
                </BaseNotification>
            </div>

            <div
                v-if="capabilities && !capabilitiesError && !isLoadingCapabilities && !inspection && !isInspecting"
                ref="fileDropZone"
                data-testid="work-document-dropzone"
                class="flex flex-col gap-2 rounded-lgr outline-offset-4"
                :class="isOverDropZone ? 'outline outline-2 outline-button-primary' : ''">
                <label for="work-document-file" class="flex flex-col gap-2">
                    <span class="body-semibold text-ink">{{ t("chat.workDocument.file.label") }}</span>
                    <input
                        id="work-document-file"
                        ref="fileInput"
                        type="file"
                        :accept="acceptedFileTypes"
                        :disabled="isBusy"
                        class="body-small text-control-text rounded-lgr border border-control-border bg-control-bg p-3 file:mr-3 file:rounded-lg file:border-0 file:bg-button-secondary file:px-3 file:py-2 file:text-button-secondary-text"
                        @change="handleFileChange" />
                </label>
                <p class="details text-ink-muted">{{ t("chat.workDocument.file.hint") }}</p>
            </div>

            <BaseCheckbox
                v-if="allowAdvanced && capabilities && !capabilitiesError && !isLoadingCapabilities && !analysis && !isReading"
                v-model="isAdvanced"
                checkbox-id="work-document-advanced"
                shape="square"
                :disabled="isBusy"
                aria-describedby="work-document-advanced-description">
                <span class="body-small-semibold text-ink">{{ t("chat.workDocument.advanced.label") }}</span>
                <span id="work-document-advanced-description" class="details text-ink-muted mt-1 block">{{ t("chat.workDocument.advanced.description") }}</span>
            </BaseCheckbox>

            <BaseNotification v-if="isOfficeDocument && officeGuidance" theme="warning" hide-dismiss>
                <template #content>
                    <div>
                        <p class="body-small-semibold">{{ officeGuidance.title }}</p>
                        <p class="body-small mt-1">{{ officeGuidance.message }}</p>
                        <p class="details mt-2">{{ t("chat.workDocument.file.officeInstructions") }}</p>
                    </div>
                </template>
            </BaseNotification>

            <div aria-live="polite" aria-atomic="true">
                <div v-if="isInspecting" class="flex items-center gap-3 text-ink">
                    <LoadingSpinner class="h-5" />
                    <span class="body-small-semibold">{{ t("chat.workDocument.file.checking") }}</span>
                </div>
                <BaseNotification v-else-if="inspectionError" theme="error" hide-dismiss>
                    <template #content>
                        <p class="body-small-semibold">{{ inspectionError }}</p>
                    </template>
                </BaseNotification>
            </div>

            <template v-if="inspection">
                <div class="flex flex-col gap-3">
                    <div class="flex items-start justify-between gap-3">
                        <p class="body-small text-ink min-w-0 break-words">
                            <span class="body-small-semibold">{{ inspection.filename }}</span>
                            <!-- eslint-disable @intlify/vue-i18n/no-raw-text -- punctuation separator -->
                            · {{ inspectedFileDescription }} · {{ formattedSelectedFileSize }}
                            <!-- eslint-enable @intlify/vue-i18n/no-raw-text -- end of punctuation separators -->
                        </p>
                        <BaseButton v-if="analysis" theme="tertiary" size="sm" :aria-label="t('chat.workDocument.file.clearLabel')" :disabled="isBusy" @click="clearAnalysis">
                            {{ t("chat.workDocument.file.clear") }}
                        </BaseButton>
                    </div>
                    <div v-if="previewBitmap" v-show="!isReading && !analysis" class="flex justify-center">
                        <canvas
                            ref="imagePreviewCanvas"
                            role="img"
                            :aria-label="t('chat.workDocument.file.previewLabel')"
                            class="fs-exclude block max-h-48 max-w-full rounded-lgr bg-surface" />
                    </div>
                </div>

                <div v-if="isAdvanced && !analysis && !isReading" class="flex flex-col gap-3">
                    <label for="work-document-focus" class="flex flex-col gap-2">
                        <span class="body-small-semibold text-ink">{{ t("chat.workDocument.focus.optionalLabel") }}</span>
                        <BaseTextarea
                            id="work-document-focus"
                            v-model:input="focusInstruction"
                            :aria-label="t('chat.workDocument.focus.label')"
                            :disabled="isBusy"
                            :placeholder="t('chat.workDocument.focus.placeholder')"
                            rows="3"
                            class="rounded-lgr border border-control-border bg-control-bg p-3" />
                    </label>
                    <p class="details text-ink-muted">{{ t("chat.workDocument.focus.example") }}</p>
                </div>
            </template>

            <div aria-live="polite" aria-atomic="true">
                <BaseNotification v-if="documentCheck || analysis" :theme="documentCheck ? (documentCheck.status === 'uncertain' ? 'warning' : 'error') : 'success'" hide-dismiss>
                    <template #content>
                        <div>
                            <p class="body-small-semibold">{{ checkResultTitle }}</p>
                            <template v-if="documentCheck">
                                <p class="body-small mt-2 whitespace-pre-wrap">{{ documentCheck.user_message }}</p>
                                <ul v-if="checkConcerns.length" class="details mt-2 list-disc pl-5">
                                    <li v-for="concern in checkConcerns" :key="concern.key">
                                        {{ concern.message }}
                                        <span v-if="concern.pages">{{ t("chat.workDocument.check.pages", { pages: concern.pages }) }}</span>
                                    </li>
                                </ul>
                            </template>
                        </div>
                    </template>
                </BaseNotification>
                <div v-if="isAdvanced && checkInputTokens !== null && documentCheck?.status !== 'blocked'" class="mt-2 text-ink-muted">
                    <div class="flex items-center gap-2">
                        <StatusChip state="neutral" size="2xs">{{ checkInputTokenLabel }}</StatusChip>
                        <BasePopover as-child side="top" align="start" :side-offset="6" :collision-padding="12" content-class="max-w-80 p-3">
                            <template #trigger>
                                <BaseButton theme="tertiary" size="sm" icon-only class="text-ink-muted" :aria-label="t('chat.workDocument.tokens.about')">
                                    <InfoIcon class="h-4 w-4" aria-hidden="true" />
                                </BaseButton>
                            </template>
                            <template #content>
                                <div class="details flex flex-col gap-2">
                                    <p>{{ t("chat.workDocument.tokens.explanation") }}</p>
                                    <p>{{ t("chat.workDocument.tokens.separate") }}</p>
                                </div>
                            </template>
                        </BasePopover>
                    </div>
                </div>
            </div>

            <div>
                <div v-if="isReading || isRefining" class="flex flex-col gap-3 rounded-lgr border border-control-border bg-surface p-4">
                    <div aria-live="polite" aria-atomic="true" class="flex items-center gap-3 text-ink">
                        <LoadingSpinner class="h-5" />
                        <span class="body-small-semibold">{{ readingStatus }}</span>
                    </div>
                    <div
                        ref="liveReading"
                        role="region"
                        :aria-label="t('chat.workDocument.reading.regionLabel')"
                        tabindex="0"
                        class="overflow-y-auto overscroll-contain rounded-lgr bg-surface-sunken p-3"
                        :class="isAdvanced ? 'max-h-60 min-h-24' : 'h-24'">
                        <p v-if="livePreview" class="body-small text-ink-muted whitespace-pre-wrap">{{ livePreview }}</p>
                        <p v-else class="body-small text-ink-muted">{{ t("chat.workDocument.reading.preparing") }}</p>
                    </div>
                    <p v-if="!isAdvanced" class="details text-ink-muted">{{ t("chat.workDocument.reading.attachHint") }}</p>
                </div>
                <BaseNotification v-else-if="analysisError" theme="error" hide-dismiss>
                    <template #content>
                        <p class="body-small-semibold">{{ analysisError }}</p>
                    </template>
                </BaseNotification>
            </div>

            <div v-if="analysis && isAdvanced && !isRefining" class="flex flex-col gap-4 rounded-lgr border border-control-border bg-surface p-4">
                <div>
                    <h3 ref="analysisHeading" tabindex="-1" class="body-small-semibold text-ink">{{ t("chat.workDocument.review.heading") }}</h3>
                    <div aria-live="polite" aria-atomic="true">
                        <p v-if="refinementCompleted" class="body-small-semibold text-ink mt-1">{{ t("chat.workDocument.review.updated") }}</p>
                    </div>
                    <BaseCollapsible v-model:open="isPreviewOpen" class="mt-2">
                        <template #trigger>
                            <span class="body-small text-ink flex items-center gap-2">
                                <i :class="isPreviewOpen ? 'bi bi-chevron-up' : 'bi bi-chevron-down'" aria-hidden="true"></i>
                                {{ t("chat.workDocument.review.preview") }}
                            </span>
                        </template>
                        <template #content>
                            <div
                                role="region"
                                :aria-label="t('chat.workDocument.review.extractedLabel')"
                                tabindex="0"
                                class="mt-2 max-h-60 overflow-y-auto overscroll-contain pr-2 md:max-h-72">
                                <p class="body-small text-ink whitespace-pre-wrap">{{ analysis.summary }}</p>
                            </div>
                        </template>
                    </BaseCollapsible>
                    <BaseCollapsible v-if="analysis.limitations.length" class="mt-3" :trigger-aria-label="limitationsLabel">
                        <template #trigger>
                            <span class="body-small-semibold text-ink underline">{{ limitationsLabel }}</span>
                        </template>
                        <template #content>
                            <BaseNotification theme="info" hide-dismiss class="mt-2">
                                <template #content>
                                    <ul class="body-small list-disc pl-5">
                                        <li v-for="(limitation, index) in analysis.limitations" :key="index">{{ limitation }}</li>
                                    </ul>
                                </template>
                            </BaseNotification>
                        </template>
                    </BaseCollapsible>
                </div>
            </div>

            <div v-if="analysis && isRefinementOpen && !isRefining" id="work-document-refinement-panel" class="flex flex-col gap-3">
                <label for="work-document-refinement" class="flex flex-col gap-2">
                    <span class="body-small-semibold text-ink">{{ t("chat.workDocument.refine.label") }}</span>
                    <BaseTextarea
                        id="work-document-refinement"
                        ref="refinementInput"
                        v-model:input="refinementInstruction"
                        :aria-label="t('chat.workDocument.refine.label')"
                        :aria-describedby="refinementError ? 'work-document-refinement-error' : undefined"
                        :disabled="isBusy"
                        rows="3"
                        class="rounded-lgr border border-control-border bg-control-bg p-3"
                        :placeholder="t('chat.workDocument.refine.placeholder')" />
                </label>
                <div aria-live="polite" aria-atomic="true">
                    <BaseNotification v-if="refinementError" theme="error" hide-dismiss>
                        <template #content>
                            <div id="work-document-refinement-error">
                                <p class="body-small-semibold">{{ refinementError }}</p>
                                <p class="details mt-1">{{ t("chat.workDocument.refine.unchanged") }}</p>
                            </div>
                        </template>
                    </BaseNotification>
                </div>
                <p class="details text-ink-muted">{{ t("chat.workDocument.refine.hint") }}</p>
            </div>
        </div>

        <template v-if="inspection" #footer>
            <div class="flex w-full flex-col gap-3">
                <div class="flex items-center gap-2">
                    <I18nT v-if="analysis" keypath="chat.workDocument.footer.onlyExtractedWithTokens" tag="p" class="details text-ink-muted">
                        <template #tokens
                            ><span class="font-mono">{{ t("chat.workDocument.tokens.projected", { count: projectedChatContextValue }) }}</span></template
                        >
                    </I18nT>
                    <p v-else class="details text-ink-muted">{{ t("chat.workDocument.footer.onlyExtracted") }}</p>
                    <BasePopover as-child side="top" align="end" :side-offset="6" :collision-padding="12" content-class="max-w-80 p-3">
                        <template #trigger>
                            <BaseButton theme="tertiary" size="sm" icon-only :aria-label="t('chat.workDocument.footer.dataHandling')">
                                <InfoIcon class="h-4 w-4" aria-hidden="true" />
                            </BaseButton>
                        </template>
                        <template #content>
                            <div class="details flex flex-col gap-2">
                                <p>{{ t("chat.workDocument.footer.dataProcessing") }}</p>
                                <p>{{ t("chat.workDocument.footer.dataRetention") }}</p>
                            </div>
                        </template>
                    </BasePopover>
                </div>

                <BaseNotification
                    v-if="!props.canAdd"
                    theme="warning"
                    hide-dismiss
                    :text="isAdvanced ? t('chat.workDocument.footer.limitAdvanced') : t('chat.workDocument.footer.limitBasic')" />
                <div v-if="isAdvanced || (!isBusy && (documentCheck || analysisError))" class="flex flex-wrap items-center justify-between gap-3">
                    <template v-if="!analysis">
                        <BaseButton theme="neutral" :disabled="isBusy" @click="clearAnalysis">{{
                            isAdvanced ? t("chat.workDocument.actions.previous") : t("chat.workDocument.actions.chooseAnother")
                        }}</BaseButton>
                        <BaseButton v-if="!documentCheck || documentCheck.status !== 'blocked'" theme="primary" :disabled="!canRead" @click="readDocument">{{
                            isAdvanced ? t("chat.workDocument.actions.read") : t("common.tryAgain")
                        }}</BaseButton>
                    </template>
                    <template v-else-if="isRefinementOpen">
                        <BaseButton theme="secondary" :disabled="isBusy" @click="cancelRefinement">{{ t("chat.workDocument.actions.cancelRefinement") }}</BaseButton>
                        <BaseButton theme="primary" :disabled="!canRefine" @click="refineDocument">{{
                            isRefining ? t("chat.workDocument.actions.updating") : t("chat.workDocument.actions.update")
                        }}</BaseButton>
                    </template>
                    <template v-else>
                        <BaseButton v-if="isAdvanced" theme="secondary" aria-controls="work-document-refinement-panel" :disabled="isBusy" @click="startRefinement">{{
                            t("chat.workDocument.actions.refine")
                        }}</BaseButton>
                        <BaseButton theme="primary" class="ml-auto" :disabled="isBusy || !props.canAdd" @click="addToChat">{{ t("chat.workDocument.actions.add") }}</BaseButton>
                    </template>
                </div>
            </div>
        </template>
    </BaseDialog>
</template>

<script setup lang="ts">
import { useDropZone } from "@vueuse/core";
import visualDocumentTutorial from "~img/visual-document-tutorial.gif";
import { useFetch } from "~vue/api";
import type { VisualDocumentContent } from "~vue/appTypes";
import BaseButton from "~vue/components/BaseButton.vue";
import BaseChip from "~vue/components/BaseChip.vue";
import BaseCollapsible from "~vue/components/BaseCollapsible.vue";
import BaseDialog from "~vue/components/BaseDialog.vue";
import BaseNotification from "~vue/components/BaseNotification.vue";
import BasePopover from "~vue/components/BasePopover.vue";
import BaseCheckbox from "~vue/components/form/BaseCheckbox.vue";
import BaseTextarea from "~vue/components/form/BaseTextarea.vue";
import StatusChip from "~vue/components/StatusChip.vue";
import InfoIcon from "~vue/icons/InfoIcon.vue";
import LoadingSpinner from "~vue/icons/LoadingSpinner.vue";
import { formatNumber } from "~vue/utils/formatNumber";
import { computed, nextTick, onBeforeUnmount, ref, shallowRef, useTemplateRef, watch } from "vue";
import { I18nT, useI18n } from "vue-i18n";

import { streamWorkDocument } from "./workDocumentStream";
import type {
    WorkDocumentAnalysisResponse,
    WorkDocumentCapabilities,
    WorkDocumentCheckResponse,
    WorkDocumentInspectionResponse,
    WorkDocumentReadFailure,
} from "./workDocumentTypes";

const props = withDefaults(
    defineProps<{ capabilitiesUrl: string; canAdd?: boolean; allowAdvanced?: boolean; prepareAttachment?: (file: VisualDocumentContent) => Promise<void> }>(),
    { canAdd: true, allowAdvanced: false },
);
const emit = defineEmits<{
    added: [VisualDocumentContent];
    "upload-error": [string];
    "read-failed": [WorkDocumentReadFailure];
    "selection-cleared": [];
}>();
const isOpen = defineModel<boolean>("open", { default: false });
const { t } = useI18n();
const pendingFile = defineModel<File | null>("file", { default: null });

const selectedFile = ref<File | null>(null);
const isAdvanced = ref(false);
const isPreviewOpen = ref(false);
const focusInstruction = ref("");
const inspection = ref<WorkDocumentInspectionResponse | null>(null);
const inspectionError = ref("");
const documentCheck = ref<WorkDocumentCheckResponse | null>(null);
const checkInputTokens = ref<number | null>(null);
const analysis = ref<WorkDocumentAnalysisResponse | null>(null);
const analysisError = ref("");
const operation = ref<"inspect" | "read" | "refine" | null>(null);
const refinementInstruction = ref("");
const refinementError = ref("");
const refinementCompleted = ref(false);
const modalHistory = ref<string[]>([]);
const isRefinementOpen = ref(false);
const refinementInput = ref<InstanceType<typeof BaseTextarea> | null>(null);
const analysisHeading = ref<HTMLElement | null>(null);
const fileInput = ref<HTMLInputElement | null>(null);
const fileDropZone = useTemplateRef("fileDropZone");
const imagePreviewCanvas = ref<HTMLCanvasElement | null>(null);
const liveReading = ref<HTMLElement | null>(null);
const previewBitmap = shallowRef<ImageBitmap | null>(null);
const readingPhase = ref<"checking" | "reading" | "refining" | null>(null);
const livePreview = ref("");
let streamController: AbortController | null = null;
let stateVersion = 0;

const {
    data: capabilities,
    error: capabilitiesError,
    isFetching: isLoadingCapabilities,
    execute: fetchCapabilities,
} = useFetch(props.capabilitiesUrl, { immediate: false }).get().json<WorkDocumentCapabilities>();

const acceptedFileTypes = ".pdf,.png,.jpg,.jpeg,.gif,.webp,.ppt,.pptx";
const officeGuidance = computed(() => capabilities.value?.office_document_guidance);
const isOfficeDocument = computed(() => /\.pptx?$/i.test(selectedFile.value?.name ?? ""));
const isAttaching = ref(false);
const isBusy = computed(() => isLoadingCapabilities.value || operation.value !== null || isAttaching.value);
const isInspecting = computed(() => operation.value === "inspect");
const isReading = computed(() => operation.value === "read");
const isRefining = computed(() => operation.value === "refine");
const progressSteps = computed(() => [
    { id: 1, label: t("chat.workDocument.progress.choose") },
    { id: 2, label: t("chat.workDocument.progress.read") },
    ...(isAdvanced.value ? [{ id: 3, label: t("chat.workDocument.progress.review") }] : []),
]);
const currentStage = computed(() => (analysis.value && isAdvanced.value ? 3 : inspection.value ? 2 : 1));
const canNavigateToStep = (step: number) => currentStage.value === 2 && !analysis.value && step === 1 && !isBusy.value;
const navigateToStep = (step: number) => {
    if (canNavigateToStep(step)) {
        void clearAnalysis();
    }
};
const canRead = computed(() => props.canAdd && !!selectedFile.value && !!inspection.value && !!capabilities.value && !isOfficeDocument.value && !isBusy.value);
const canRefine = computed(() => isAdvanced.value && !!analysis.value && !!selectedFile.value && !!refinementInstruction.value.trim() && !isBusy.value);
const checkResultTitle = computed(() =>
    documentCheck.value
        ? documentCheck.value.status === "blocked"
            ? t("chat.workDocument.check.blocked")
            : t("chat.workDocument.check.uncertain")
        : t("chat.workDocument.check.passed"),
);
const checkConcerns = computed(() => {
    const check = documentCheck.value;
    if (!check) {
        return [];
    }
    const concerns = check.findings
        .filter((finding) => finding.status !== "clear")
        .map((finding) => ({
            key: finding.key,
            message: finding.user_reason,
            pages: [...new Set(finding.evidence.flatMap((item) => (item.page === null ? [] : [item.page])))].map((page) => t("chat.workDocument.check.page", { page })).join(", "),
        }));
    if (check.prompt_injection_detected) {
        concerns.unshift({ key: "prompt-injection", message: check.prompt_injection_user_message || check.user_message, pages: "" });
    }
    [...(check.safety_coverage?.limitations ?? []), ...(check.limitations ?? [])].forEach((message, index) => {
        if (message && !concerns.some((concern) => concern.message === message)) {
            concerns.push({ key: `coverage-${index}`, message, pages: "" });
        }
    });
    return concerns.filter((concern) => concern.message !== check.user_message);
});
const formattedSelectedFileSize = computed(() => {
    const bytes = selectedFile.value?.size ?? 0;
    return bytes >= 1024 * 1024
        ? t("chat.workDocument.file.sizeMb", { size: formatNumber(bytes / (1024 * 1024), "one-decimal") })
        : t("chat.workDocument.file.sizeKb", { size: formatNumber(Math.max(1, Math.round(bytes / 1024))) });
});
const inspectedFileDescription = computed(() => {
    const pages = inspection.value?.page_count;
    return pages === null ? t("chat.workDocument.file.oneImage") : t("chat.workDocument.file.pages", { count: pages ?? 0 }, pages ?? 0);
});
const checkInputTokenLabel = computed(() =>
    checkInputTokens.value === null ? t("chat.workDocument.tokens.unavailable") : t("chat.workDocument.tokens.input", { count: formatNumber(checkInputTokens.value, "compact") }),
);
const limitationsLabel = computed(() => t("chat.workDocument.review.limitations", { count: analysis.value?.limitations.length ?? 0 }));
const projectedChatContextValue = computed(() => (analysis.value ? `~${formatNumber(analysis.value.context_token_count, "compact")}` : ""));
const readingStatus = computed(() => {
    if (readingPhase.value === "checking") {
        return t("chat.workDocument.reading.checking");
    }
    if (readingPhase.value === "refining") {
        return t("chat.workDocument.reading.refining");
    }
    return t("chat.workDocument.reading.reading");
});

const clearPreview = () => {
    previewBitmap.value?.close();
    previewBitmap.value = null;
};
const resetFileSelection = () => {
    stateVersion += 1;
    // A reset can outlive a reader-fallback upload the composer started for the previous
    // selection — the emit lets it invalidate that upload's result.
    emit("selection-cleared");
    streamController?.abort();
    streamController = null;
    selectedFile.value = null;
    inspection.value = null;
    inspectionError.value = "";
    documentCheck.value = null;
    checkInputTokens.value = null;
    analysis.value = null;
    isPreviewOpen.value = false;
    analysisError.value = "";
    readingPhase.value = null;
    livePreview.value = "";
    operation.value = null;
    focusInstruction.value = "";
    refinementInstruction.value = "";
    refinementError.value = "";
    refinementCompleted.value = false;
    modalHistory.value = [];
    isRefinementOpen.value = false;
    clearPreview();
    if (fileInput.value) {
        fileInput.value.value = "";
    }
};
watch(
    isOpen,
    (open) => {
        if (open) {
            fetchCapabilities();
        } else {
            pendingFile.value = null;
            resetFileSelection();
            isAdvanced.value = false;
        }
    },
    { immediate: true },
);
onBeforeUnmount(resetFileSelection);

const drawImagePreview = () => {
    if (!previewBitmap.value || !imagePreviewCanvas.value) {
        return;
    }
    const scale = Math.min(1, 800 / previewBitmap.value.width, 384 / previewBitmap.value.height);
    const canvas = imagePreviewCanvas.value;
    canvas.width = Math.round(previewBitmap.value.width * scale);
    canvas.height = Math.round(previewBitmap.value.height * scale);
    canvas.getContext("2d")?.drawImage(previewBitmap.value, 0, 0, canvas.width, canvas.height);
};
const emitInspectFailure = (file: File, data: { error?: string } | null, error: unknown) => {
    inspectionError.value = data?.error ?? (error as { message?: string } | null)?.message ?? t("chat.workDocument.errors.checkFailed");
    // 4xx and error bodies are explicit rejections; 5xx and network failures are
    // transient like read failures and may fall back to the text parser upstream.
    const statusCode = (error as { statusCode?: number } | null)?.statusCode ?? null;
    const rejected = Boolean(data?.error) || (statusCode !== null && statusCode < 500);
    emit("read-failed", { file, code: rejected ? "inspect_failed" : "read_failed", blocked: false });
};

const selectFile = async (file: File | null) => {
    resetFileSelection();
    selectedFile.value = file;
    if (!file || isOfficeDocument.value || !capabilities.value) {
        return;
    }
    if (file.size > capabilities.value.max_source_file_bytes) {
        inspectionError.value = t("chat.workDocument.errors.tooLarge", { size: Math.floor(capabilities.value.max_source_file_bytes / 1_000_000) });
        emit("read-failed", { file, code: "file_too_large", blocked: false });
        return;
    }
    const requestVersion = stateVersion;
    operation.value = "inspect";
    const formData = new FormData();
    formData.append("file", file);
    const { data, error } = await useFetch(capabilities.value.inspect_url).post(formData).json<WorkDocumentInspectionResponse & { error?: string }>();
    if (requestVersion !== stateVersion) {
        return;
    }
    if (error.value || !data.value || data.value.error) {
        operation.value = null;
        emitInspectFailure(file, data.value, error.value);
        return;
    }
    // Inspect dimensions before decoding a browser preview of a potentially huge image.
    if (isAdvanced.value && file.type.startsWith("image/")) {
        try {
            const bitmap = await createImageBitmap(file);
            if (requestVersion !== stateVersion) {
                bitmap.close();
                return;
            }
            previewBitmap.value = bitmap;
        } catch {
            /* A preview is optional; the server has validated the file. */
        }
        if (requestVersion !== stateVersion) {
            return;
        }
    }
    operation.value = null;
    inspection.value = data.value;
    await nextTick();
    drawImagePreview();
    if (!isAdvanced.value && requestVersion === stateVersion) {
        await readDocument();
    }
};
const handleFileChange = (event: Event) => selectFile((event.target as HTMLInputElement).files?.[0] ?? null);
const { isOverDropZone } = useDropZone(fileDropZone, {
    onDrop: (files) => {
        if (files?.length && !isBusy.value) {
            void selectFile(files[0]);
            if (files.length > 1) {
                emit("upload-error", t("chat.workDocument.errors.multipleDropped"));
            }
        }
    },
    preventDefaultForUnhandled: true,
});
const showReading = async () => {
    await nextTick();
    analysisHeading.value?.focus({ preventScroll: true });
    analysisHeading.value?.scrollIntoView({ block: "start" });
};
const processDocument = async (refinement: boolean) => {
    if (!selectedFile.value || !capabilities.value || isBusy.value) {
        return;
    }
    const requestVersion = stateVersion;
    const instruction = refinementInstruction.value.trim();
    const formData = new FormData();
    formData.append("file", selectedFile.value);
    formData.append("focus_instruction", isAdvanced.value ? focusInstruction.value.trim() : "");
    if (refinement && analysis.value) {
        formData.append("current_reading", analysis.value.summary);
        formData.append("refinement_instruction", instruction);
        formData.append("modal_history", JSON.stringify(modalHistory.value));
    }
    operation.value = refinement ? "refine" : "read";
    readingPhase.value = "checking";
    livePreview.value = "";
    refinementError.value = "";
    analysisError.value = "";
    if (!refinement) {
        documentCheck.value = null;
        analysis.value = null;
    }
    const url = refinement ? capabilities.value.refine_url : capabilities.value.read_url;
    streamController = new AbortController();
    const streamResult: { value: WorkDocumentAnalysisResponse | null } = { value: null };
    let failed = false;
    try {
        await streamWorkDocument(
            url,
            formData,
            (event) => {
                if (requestVersion !== stateVersion) {
                    return;
                }
                if (event.type === "phase") {
                    readingPhase.value = event.phase;
                } else if (event.type === "preview") {
                    livePreview.value = event.text;
                    nextTick(() => {
                        if (liveReading.value) {
                            liveReading.value.scrollTop = liveReading.value.scrollHeight;
                        }
                    });
                } else if (event.type === "complete") {
                    streamResult.value = event.result;
                    livePreview.value = event.result.summary;
                } else if (event.type === "error") {
                    failed = true;
                    if (refinement) {
                        refinementError.value = event.error;
                    } else {
                        documentCheck.value = event.check ?? null;
                        checkInputTokens.value = event.check_input_tokens ?? null;
                        analysisError.value = documentCheck.value ? "" : event.error;
                        if (selectedFile.value) {
                            emit("read-failed", { file: selectedFile.value, code: event.code ?? null, blocked: !!event.check });
                        }
                    }
                    livePreview.value = "";
                }
            },
            streamController.signal,
        );
    } catch (error) {
        if (requestVersion !== stateVersion || (error instanceof DOMException && error.name === "AbortError")) {
            return;
        }
        failed = true;
        livePreview.value = "";
        const message = error instanceof Error ? error.message : t("chat.workDocument.errors.readFailed");
        if (refinement) {
            refinementError.value = message;
        } else {
            analysisError.value = message;
            if (selectedFile.value) {
                emit("read-failed", {
                    file: selectedFile.value,
                    code: (error as { code?: string | null }).code ?? null,
                    blocked: Boolean((error as { check?: unknown }).check),
                });
            }
        }
    } finally {
        if (requestVersion === stateVersion) {
            operation.value = null;
            readingPhase.value = null;
            streamController = null;
        }
    }
    if (requestVersion !== stateVersion || failed) {
        return;
    }
    const completedResult = streamResult.value;
    if (!completedResult) {
        const message = t("chat.workDocument.errors.incomplete");
        if (refinement) {
            refinementError.value = message;
        } else {
            analysisError.value = message;
            if (selectedFile.value) {
                emit("read-failed", { file: selectedFile.value, code: "read_failed", blocked: false });
            }
        }
        return;
    }
    // Refines return no text layer server-side; carry the read's over so the
    // refined analysis keeps the verbatim section.
    analysis.value = refinement
        ? { ...completedResult, extracted_text: analysis.value?.extracted_text ?? "", text_truncated: analysis.value?.text_truncated ?? false }
        : completedResult;
    isPreviewOpen.value = false;
    documentCheck.value = null;
    checkInputTokens.value = completedResult.check_input_tokens;
    if (refinement) {
        modalHistory.value.push(instruction);
        refinementInstruction.value = "";
        isRefinementOpen.value = false;
        refinementCompleted.value = true;
    }
    if (isAdvanced.value) {
        await showReading();
    } else {
        addToChat();
    }
};
const readDocument = () => {
    if (canRead.value) {
        return processDocument(false);
    }
};
const refineDocument = () => {
    if (canRefine.value) {
        return processDocument(true);
    }
};
const startRefinement = async () => {
    refinementCompleted.value = false;
    isRefinementOpen.value = true;
    await nextTick();
    refinementInput.value?.textareaRef?.focus();
};
const cancelRefinement = async () => {
    refinementInstruction.value = "";
    refinementError.value = "";
    isRefinementOpen.value = false;
    await showReading();
};
const clearAnalysis = async () => {
    resetFileSelection();
    await nextTick();
    fileInput.value?.focus();
};
const addToChat = async () => {
    if (!analysis.value || !selectedFile.value || !props.canAdd || isBusy.value || isRefinementOpen.value) {
        return;
    }
    const file: VisualDocumentContent = {
        kind: "visual_document",
        draftId: crypto.randomUUID(),
        name: selectedFile.value.name,
        type: selectedFile.value.name.split(".").pop()?.toLowerCase() ?? "",
        content: analysis.value.context_text,
        description: "",
        limitations: analysis.value.limitations,
        extracted_text: analysis.value.extracted_text ?? "",
        text_truncated: analysis.value.text_truncated ?? false,
    };
    const version = stateVersion;
    try {
        isAttaching.value = true;
        await props.prepareAttachment?.(file);
        if (version !== stateVersion || !isOpen.value) {
            return;
        }
        emit("added", file);
        isOpen.value = false;
    } catch (error) {
        if (version === stateVersion && isOpen.value) {
            analysisError.value = error instanceof Error ? error.message : t("chat.workDocument.errors.readFailed");
            emit("upload-error", analysisError.value);
        }
    } finally {
        isAttaching.value = false;
    }
};

watch(isAdvanced, (advanced) => {
    if (!advanced && canRead.value && !analysis.value) {
        void readDocument();
    }
});

watch(
    [isOpen, pendingFile, capabilities, isLoadingCapabilities, capabilitiesError],
    ([open, file, loaded, loading, error]) => {
        if (open && file && error) {
            pendingFile.value = null;
            emit("read-failed", { file, code: "capabilities_unavailable", blocked: false });
            return;
        }
        if (open && file && loaded && !loading && !error) {
            pendingFile.value = null;
            void selectFile(file);
        }
    },
    { immediate: true },
);
</script>
